/** The smart inbox — the recipient's read (M14, FR-6). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */
import { type RawError, bareHandle, call, get, nullableNum, num, post, strings } from "./core";
import { toAttachment, type Attachment } from "./attachment";
import {
  type KnockGate,
  type KnockReadFailureCode,
  type MessageRole,
  ROLES,
  gateOf,
  knockReadFailure,
  ownOriginAvatar,
} from "./knock";
// --- the smart inbox (M14, PRD FR-6 / §2.3) ---------------------------------

/**
 * The recipient's own surface — the drill-down that the Morning Nob makes
 * optional (§2.3). Here the recipient SEES what the sender structurally cannot:
 * the score, the percentile, the band, the summary written about the sender.
 * Everything below normalises tolerantly — the inbox contract is pinned but the
 * API is still moving, and a missing optional field must never blank a card.
 */

export type InboxView = "top" | "recent" | "saved" | "actioned" | "everything";

export const INBOX_VIEWS: readonly InboxView[] = [
  "top",
  "recent",
  "saved",
  "actioned",
  "everything",
];

/**
 * The band the recipient is allowed to read plainly (opposite of the sender).
 *
 * BOTH SPELLINGS THE SERVER HAS (`apps/api/src/knocks/bands.ts`): the five
 * percentile bands a ranked door produces, and the three absolute tiers a door
 * below `MIN_RANK_SAMPLE` produces instead (E-63). A cold-start door is every
 * new door, so the tiers were not an edge case — they were the common case,
 * and the parser's fallback called all three of them "Middle" until
 * 2026-09-13. A 99 and a 12 must not share a word.
 */
export type InboxBand =
  | "TOP_5"
  | "TOP_25"
  | "MIDDLE"
  | "BOTTOM_25"
  | "BOTTOM_5"
  | "BOTTOM"
  | "TIER_STRONG"
  | "TIER_REASONABLE"
  | "TIER_WEAK";

export const INBOX_BANDS: readonly InboxBand[] = [
  "TOP_5",
  "TOP_25",
  "MIDDLE",
  "BOTTOM_25",
  "BOTTOM_5",
  "BOTTOM",
  "TIER_STRONG",
  "TIER_REASONABLE",
  "TIER_WEAK",
];

export type RecipientSender = {
  /** Empty for an email-only sender — they have no X identity to name. */
  handle: string;
  display_name: string | null;
  /** null when there is no X identity behind the sender: unknown, not zero. */
  followers: number | null;
  account_age_days: number | null;
  company_url: string | null;
  /** A path on OUR origin or nothing — the same gate every door picture passes. */
  avatar: string | null;
  /** What the door's rules asked for and the sender gave. Never their email. */
  contact: Record<string, string>;
};

export type RecipientStanding = {
  band: InboxBand;
  /** null when the door has too few scored knocks to rank (E-63). */
  percentile: number | null;
  raw_score: number;
  /** The doorman's ABSOLUTE bar, server-derived — bands are relative. */
  cleared_bar: boolean;
};

export type InboxReceipts = {
  verified_claims: number;
  refuted_claims: number;
  /**
   * How many claims a check ran on, whatever it found (wave 83). Only the
   * drill-down counts it; on a list row it is null — not counted, never zero.
   */
  checked_claims: number | null;
  pages_reviewed: number;
  sources_checked: number;
};

/**
 * WHY NOB DID NOT RECOMMEND A KNOCK (ruling R-0925-1 (2)) — the server's code,
 * worded on the owner's desk (`inbox/labels.ts#nobCallReason`). Owner copy
 * only: nothing on the sender's side of the wire carries it.
 */
export type NobCallReason =
  | "stopped"
  | "door_closed"
  | "minor"
  | "strict_receipt"
  | "strict_story_changed"
  | "exclusion"
  | "procedure_refuse"
  | "below_bar";

const NOB_CALL_REASONS: readonly NobCallReason[] = [
  "stopped",
  "door_closed",
  "minor",
  "strict_receipt",
  "strict_story_changed",
  "exclusion",
  "procedure_refuse",
  "below_bar",
];

/** Nob's call on a knock, or null while there is none yet (no read, no stop). */
export type NobCall = {
  recommended: boolean;
  reason: NobCallReason | null;
  /** The sentence behind the reason, where there is one: the owner's own line. */
  line: string | null;
};

function nobCallOf(raw: unknown): NobCall | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  if (typeof c["recommended"] !== "boolean") return null;
  const reason = NOB_CALL_REASONS.find((known) => known === c["reason"]) ?? null;
  // A "no" with a reason this client cannot word is still a no, said plainly.
  return {
    recommended: c["recommended"],
    reason: c["recommended"] ? null : (reason ?? "below_bar"),
    line: typeof c["line"] === "string" && c["line"].trim() ? c["line"].trim() : null,
  };
}

/**
 * What the recipient reads on a knock (FR-6.3). The score, the percentile and
 * the summary are the recipient's to see — the sender's `toSenderView` strips
 * every one of them (§5.4 rule 5).
 *
 * `spent_credits` and its tooltip stood here — the costly signal a sender's
 * spend was meant to be, parsed on every card and never once rendered on one.
 * Ruling R-0916-1 deleted what it counted, and a unit figure on a recipient's
 * card would be a capacity in disguise anyway (ruling C2). The receipts below
 * are what a costly signal looks like now: what the doorman DID, not what it
 * cost.
 */
export type RecipientKnock = {
  id: string;
  sender: RecipientSender;
  standing: RecipientStanding;
  summary: string;
  /**
   * The doorman's brief written FOR the owner, on its own (wave 83). `summary`
   * prefers the one-line card summary; the desk's ask strip wants the brief,
   * which is updated with every read where the knocker's first message is not.
   */
  summary_for_recipient: string | null;
  /** Nob's call (R-0925-1 (2)). Null until there is a read or a stop. */
  nob_call: NobCall | null;
  matched_categories: string[];
  receipts: InboxReceipts;
  has_booking: boolean;
  has_attachments: boolean;
  /**
   * WHERE THIS PERSON STANDS, and the only fact that says it (§3). The five
   * verbs move it and nothing else does; the doorman never does. `declined` is
   * what "Not for me" wrote, `let_in` what "Open the door" wrote, and a
   * `blocked` row does not reach this list at all.
   */
  gate: KnockGate;
  /**
   * The doorman has published a read (§6.2). It is what the R-0905-5 (b)
   * allowlist is keyed on now: a row with no assessment carries no standing, no
   * percentile, no summary, no categories and no money, and the leak test says
   * so. It is NOT a gate on the verbs — those are offered at every moment (§3).
   */
  assessed: boolean;
  /**
   * How many reads have been published (§5.2). It moves when the doorman
   * changes its mind on new evidence, and a declined thread's activity and
   * unread move only when it does.
   */
  assessment_seq: number;
  /** The guard stopped the doorman listening (§8). The gate did not move. */
  guard_closed: boolean;
  /** Why the guard stopped — the reason code, and only on a guard-closed row. */
  guard_reason: string | null;
  replied: boolean;
  /**
   * The owner pressed Save (FR-6.4). It is an OVERRIDE, not a compliment: a
   * knock that missed the bar and was saved anyway stands in Passed on the
   * strength of this flag alone, which is why it is read separately from the
   * standing rather than folded into it.
   */
  saved: boolean;
  /**
   * The owner rated the ranking unhelpful and said this one should not have
   * been surfaced (owner, 2026-08-26). The mirror of `saved`: an override
   * DOWNWARDS, read separately from the standing because the standing is still
   * whatever the doorman made it — the doorman was not overruled here, it was
   * corrected, and those are different sentences.
   */
  demoted: boolean;
  /**
   * The owner put this conversation away (ruling R-0828-1).
   *
   * Neither an override nor a judgement of anybody — the two flags above are
   * things said about the SENDER, and this is a thing said about the reader's
   * own list. The deleted case has no flag here because a deleted copy never
   * arrives: the server filters it out of every read this viewer makes.
   */
  archived: boolean;
  created_at: string | null;
};

/**
 * Who said it, on the RECIPIENT'S side of a knock — two roles wider than the
 * sender's (`MessageRole`), and the two extra ones are the whole of ruling
 * 2026-08-25 #9: `owner` is the door's owner talking to their own Nob about
 * this knock, `doorman_to_owner` is the Nob answering them.
 *
 * The widening is deliberately HERE and not on `MessageRole`. That type is what
 * the sender's own thread parses with, and the server excludes both of these
 * roles from every sender-facing read in SQL — so a sender-side parser that
 * could name them would be describing rows it must never be sent.
 */
export type InboxMessageRole = MessageRole | "owner" | "doorman_to_owner";

export const INBOX_ROLES: InboxMessageRole[] = [...ROLES, "owner", "doorman_to_owner"];

/**
 * The two roles that are the house talking to itself. Never the sender's.
 *
 * `recipient` IS NOT ONE OF THEM, and the near-miss is worth naming: it is also
 * the owner speaking, and it also sits on the right of the screen. The
 * difference is who it was said TO. `owner` is a remark ABOUT the person at the
 * door; `recipient` is a message to them, and they read it. It lives on
 * `MessageRole` — the sender's own type — for exactly that reason.
 */
export function isHouseSide(role: InboxMessageRole): boolean {
  return role === "owner" || role === "doorman_to_owner";
}

export type InboxTranscriptLine = {
  seq: number;
  role: InboxMessageRole;
  content: string;
  /**
   * R-0910-11: a `doorman` line this owner asked their own Nob to put to the
   * person at the door. It is the OWNER'S fact and only theirs — the desk reads
   * it back as `Asked them: "…"` so they know the question went, while the
   * sender's room draws the same row as any doorman bubble.
   */
  relayed?: boolean;
  created_at: string | null;
  /**
   * The knocker's voice note this line was said in (wave 76), which rides in
   * the detail's `attachments`: the drill-down draws the two as ONE bubble,
   * the player with the words under it (wave 78). Absent or null otherwise.
   */
  voice_attachment_id?: string | null;
};

/**
 * The detail adds the hearing itself and what the sender put into it. The
 * files ride as the same six-field shape the sender's own thread renders
 * (`attachment.ts`), so a voice note plays — and reads (R-0905-4) — the same
 * way on both sides of the door.
 */
export type RecipientKnockDetail = RecipientKnock & {
  transcript: InboxTranscriptLine[];
  attachments: RecipientAttachment[];
};

/**
 * A file as the OWNER reads it (wave 83): the six shared fields, and whether
 * the doorman read it — so three documents with one of them read no longer
 * show as a page total.
 */
export type RecipientAttachment = Attachment & { read: boolean };

function inboxBandOf(value: unknown): InboxBand {
  return typeof value === "string" && (INBOX_BANDS as readonly string[]).includes(value)
    ? (value as InboxBand)
    : "MIDDLE";
}

/**
 * The parser for `toRecipientView` (apps/api/src/db/views/recipientView.ts),
 * field for field, under the SERVER'S names. This function once read names of
 * its own invention — `followers` for `followers_count`, `verified_claims` for
 * `claims_verified` — and the `num()` fallbacks turned every miss into a
 * plausible zero: a figure read "0" on every card and nobody's test noticed,
 * because the fixtures were written to this file's imagination rather than to
 * the server. Hence two rules now: parse only the server's spelling (a second
 * accepted name is a place for drift to hide), and the contract test in
 * `inbox-contract.test.ts` round-trips the real serializer through this
 * parser so a renamed field fails a test instead of a demo.
 *
 * Exported for that test — production code reaches it through `fetchInbox`.
 */
export function normaliseRecipientKnock(raw: unknown): RecipientKnock | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const id = typeof b["id"] === "string" ? b["id"] : "";
  if (!id) return null; // a knock with no id is a knock nothing can act on

  const senderRaw = (b["sender"] && typeof b["sender"] === "object" ? b["sender"] : {}) as Record<
    string,
    unknown
  >;
  const standingRaw = (
    b["standing"] && typeof b["standing"] === "object" ? b["standing"] : {}
  ) as Record<string, unknown>;
  const receiptsRaw = (
    b["receipts"] && typeof b["receipts"] === "object" ? b["receipts"] : {}
  ) as Record<string, unknown>;
  const actionRaw = (b["action"] && typeof b["action"] === "object" ? b["action"] : {}) as Record<
    string,
    unknown
  >;

  // The card's line is the ≤200-char brief the doorman wrote for the recipient.
  const summary =
    typeof b["summary_for_recipient"] === "string" && b["summary_for_recipient"]
      ? b["summary_for_recipient"]
      : "";
  const company =
    typeof senderRaw["company_url"] === "string" && senderRaw["company_url"].trim()
      ? (senderRaw["company_url"] as string)
      : null;
  return {
    id,
    sender: {
      handle: typeof senderRaw["handle"] === "string" ? bareHandle(senderRaw["handle"]) : "",
      display_name:
        typeof senderRaw["display_name"] === "string" && senderRaw["display_name"].trim()
          ? (senderRaw["display_name"] as string)
          : null,
      followers: nullableNum(senderRaw["followers_count"]),
      account_age_days: nullableNum(senderRaw["account_age_days"]),
      company_url: company,
      avatar: ownOriginAvatar(senderRaw["avatar"]),
      contact: ((): Record<string, string> => {
        const raw = senderRaw["contact"];
        if (!raw || typeof raw !== "object") return {};
        const out: Record<string, string> = {};
        for (const [key, value] of Object.entries(raw)) {
          if (typeof value === "string" && value.trim()) out[key] = value;
        }
        return out;
      })(),
    },
    standing: {
      band: inboxBandOf(standingRaw["band"]),
      percentile: nullableNum(standingRaw["percentile"]),
      raw_score: num(standingRaw["raw_score"]),
      cleared_bar: standingRaw["cleared_bar"] === true,
    },
    summary,
    summary_for_recipient:
      typeof b["summary_for_recipient"] === "string" && b["summary_for_recipient"].trim()
        ? b["summary_for_recipient"].trim()
        : null,
    nob_call: nobCallOf(b["nob_call"]),
    matched_categories: strings(b["matched_categories"]),
    receipts: {
      verified_claims: num(receiptsRaw["claims_verified"]),
      refuted_claims: num(receiptsRaw["claims_refuted"]),
      checked_claims: nullableNum(receiptsRaw["claims_checked"]),
      pages_reviewed: num(receiptsRaw["pages_reviewed"]),
      sources_checked: num(receiptsRaw["sources_checked"]),
    },
    has_booking: b["has_booking_link"] === true,
    has_attachments: b["has_attachments"] === true,
    // The gate is the server's, and an unreadable one is `at_door` — the value
    // that claims nothing was decided, so no surface reads a decision that was
    // never made (see `gateOf`).
    gate: gateOf(b["gate"]),
    assessed: b["assessed"] === true,
    assessment_seq: num(b["assessment_seq"]),
    guard_closed: b["guard_closed"] === true,
    // Only on a guard-closed row, and the server sends null otherwise. Read
    // strictly: a reason on a row the guard never touched would be a stray.
    guard_reason:
      b["guard_closed"] === true && typeof b["guard_reason"] === "string" && b["guard_reason"]
        ? (b["guard_reason"] as string)
        : null,
    replied: actionRaw["replied"] === true,
    saved: actionRaw["saved"] === true,
    demoted: actionRaw["demoted"] === true,
    archived: actionRaw["archived"] === true,
    created_at: typeof b["received_at"] === "string" ? b["received_at"] : null,
  };
}

function normaliseTranscript(raw: unknown): InboxTranscriptLine[] {
  const out: InboxTranscriptLine[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const m = item as Record<string, unknown>;
      const role = INBOX_ROLES.find((known) => known === m["role"]);
      const content = typeof m["content"] === "string" ? m["content"] : "";
      if (!role || !content.trim()) continue;
      out.push({
        seq: num(m["seq"]),
        role,
        content,
        relayed: m["relayed"] === true,
        created_at: typeof m["created_at"] === "string" ? m["created_at"] : null,
        voice_attachment_id:
          typeof m["voice_attachment_id"] === "string" ? m["voice_attachment_id"] : null,
      });
    }
  }
  // Order is the server's contract; sorting locally costs nothing and means a
  // transcript that arrives out of order still reads as a conversation.
  out.sort((a, b) => a.seq - b.seq);
  return out;
}

/** A knock that is not yours is a knock that does not exist (404, either way). */
export type InboxResult<T> = ({ ok: true } & T) | { ok: false; code: KnockReadFailureCode };

export type InboxQuery = {
  view: InboxView;
  cursor?: string | null;
  band?: InboxBand | null;
  has_attachments?: boolean;
  has_booking?: boolean;
  // `spent_min`/`spent_max` stood here — a floor and a ceiling on what a sender
  // had spent, to narrow the list by it. No filter row ever sent one, and
  // ruling R-0916-1 deleted the figure they compared against.
  /** `null` is "either"; `true`/`false` are the two halves of the toggle. */
  replied?: boolean | null;
  q?: string;
};

export type InboxPage = { items: RecipientKnock[]; next_cursor: string | null };

function inboxQueryString(query: InboxQuery): string {
  const params = new URLSearchParams();
  params.set("view", query.view);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.band) params.set("band", query.band);
  if (query.has_attachments) params.set("has_attachments", "true");
  if (query.has_booking) params.set("has_booking", "true");
  if (query.replied === true) params.set("replied", "true");
  if (query.replied === false) params.set("replied", "false");
  if (query.q && query.q.trim()) params.set("q", query.q.trim());
  return params.toString();
}

export async function fetchInbox(query: InboxQuery): Promise<InboxResult<{ page: InboxPage }>> {
  const r = await call(() => get(`/api/v1/inbox?${inboxQueryString(query)}`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return knockReadFailure(r.res.status);
  const b = r.body as Record<string, unknown>;
  const rawItems = Array.isArray(b["items"]) ? b["items"] : [];
  const items: RecipientKnock[] = [];
  for (const item of rawItems) {
    const knock = normaliseRecipientKnock(item);
    if (knock) items.push(knock);
  }
  const cursor = b["next_cursor"];
  return {
    ok: true,
    page: { items, next_cursor: typeof cursor === "string" && cursor ? cursor : null },
  };
}

export async function fetchInboxKnock(
  id: string,
): Promise<InboxResult<{ knock: RecipientKnockDetail }>> {
  const r = await call(() => get(`/api/v1/inbox/knock/${encodeURIComponent(id)}`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return knockReadFailure(r.res.status);
  const base = normaliseRecipientKnock(r.body);
  if (!base) return { ok: false, code: "UNKNOWN" };
  const body = r.body as { transcript?: unknown; attachments?: unknown };
  const transcript = normaliseTranscript(body.transcript);
  const attachments = Array.isArray(body.attachments)
    ? body.attachments.flatMap((a) => {
        const raw = a && typeof a === "object" ? (a as Record<string, unknown>) : null;
        const parsed = raw ? toAttachment(raw) : null;
        return parsed && raw ? [{ ...parsed, read: raw["read"] === true }] : [];
      })
    : [];
  return { ok: true, knock: { ...base, transcript, attachments } };
}

export type InboxAction = "reply" | "book" | "save" | "not_interested" | "block";

/** The reasons a recipient can give about THE RANKING (FR-6.4, FR-8.2). */
export type InboxReasonChip =
  | "shouldnt_surface"
  | "ranked_too_low"
  | "summary_wrong"
  | "wrong_category"
  | "good_sender_bad_timing"
  | "other";

/**
 * The reasons a recipient can give for a DISMISSAL (owner item 10, 2026-08-26).
 * A different question from the one above, so a different vocabulary; the
 * server refuses anything outside it on a `not_interested`.
 */
export type InboxDismissChip =
  | "shouldnt_passed"
  | "not_interested"
  | "not_enough_evidence"
  | "spam_scam"
  | "good_sender_bad_timing"
  | "other";

export type InboxActionInput = {
  knock_id: string;
  action: InboxAction;
  reason_chip?: InboxReasonChip | InboxDismissChip;
  note?: string;
};

/**
 * Every action is reversible for 30 seconds (FR-6.4), so success carries the
 * token that reverses it and when that window shuts. `booking_url` is present
 * for a `book` — the sender's own link, opened in a new tab and touching no
 * calendar (FR-6.5).
 */
export type InboxActionResult =
  | {
      ok: true;
      action: InboxAction;
      undo_token: string;
      expires_at: string | null;
      booking_url: string | null;
      /**
       * THREE-VALUED, AND THE MIDDLE VALUE IS THE POINT (F-58, ruling R-0914-2).
       *
       * `undefined` — the sender left no address, so nothing was relayed and
       * nothing is owed an explanation. `true` — the masked copy went. `false` —
       * it was tried and the provider refused, which is not a failed action: the
       * door is open and the note is in the thread. It reads as a note on the
       * card rather than an error, because the only thing that did not happen
       * is the copy to their mailbox.
       */
      reply_sent?: boolean;
    }
  /**
   * `code` IS THE SERVER'S OWN CODE whenever the server named one —
   * `KNOCK_NOT_FOUND`, `REPLY_BODY_REQUIRED`, `NO_BOOKING_LINK` — and
   * `"UNKNOWN"` is kept for the two cases with nothing to pass on: a 5xx and a
   * dead connection. It used to collapse every refusal into `"UNKNOWN"`, which
   * is how a door that answered clearly came to read as "Nob is not answering".
   */
  | { ok: false; code: string };

/** The code the api named, or `"UNKNOWN"` when it named none (a 5xx, a proxy). */
function refusalCode(body: unknown): string {
  const code = (body as RawError).error?.code;
  return typeof code === "string" && code ? code : "UNKNOWN";
}

export async function submitInboxAction(input: InboxActionInput): Promise<InboxActionResult> {
  const body: Record<string, unknown> = { knock_id: input.knock_id, action: input.action };
  if (input.reason_chip) body["reason_chip"] = input.reason_chip;
  if (input.note && input.note.trim()) body["note"] = input.note.trim();
  const r = await call(() => post("/api/v1/inbox/action", body));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    return { ok: false, code: refusalCode(r.body) };
  }
  const b = r.body as Record<string, unknown>;
  const booking = b["booking_url"];
  return {
    ok: true,
    action: input.action,
    undo_token: typeof b["undo_token"] === "string" ? b["undo_token"] : "",
    expires_at: typeof b["expires_at"] === "string" ? b["expires_at"] : null,
    // The sender's own URL, left exactly as given: it is external, not ours.
    booking_url: typeof booking === "string" && booking.trim() ? booking.trim() : null,
    // Absent stays absent — see the type. A key that defaulted to `true` would
    // turn "there was nobody to write to" into "it went".
    ...(typeof b["reply_sent"] === "boolean" ? { reply_sent: b["reply_sent"] } : {}),
  };
}

export type InboxUndoResult = { ok: true } | { ok: false; code: "UNAUTHENTICATED" | "UNKNOWN" };

export async function undoInboxAction(input: { undo_token: string }): Promise<InboxUndoResult> {
  // The wire field is `action_id`: the undo token IS the action id (an opaque
  // handle, `apps/api/src/inbox/actions.ts`), and the undo schema validates it
  // under that name. Sending it as `undo_token` was a silent 400 for as long
  // as the optimistic "Undone." hid the failure.
  const r = await call(() => post("/api/v1/inbox/action/undo", { action_id: input.undo_token }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  return { ok: true };
}

/**
 * One person the owner has shut out (FR-6.4), as the Blocked-users list reads
 * them. `block_id` is a blocklist ROW id and the only handle the client holds —
 * the sender's user id never crosses the wire.
 */
export type BlockedSender = {
  block_id: string;
  /** Bare, no `@`. Empty for an email-only sender with no X identity. */
  handle: string;
  display_name: string;
  blocked_at: string | null;
};

export type BlockedListResult =
  { ok: true; blocked: BlockedSender[] } | { ok: false; code: "UNAUTHENTICATED" | "UNKNOWN" };

export async function fetchBlockedSenders(): Promise<BlockedListResult> {
  const r = await call(() => get("/api/v1/inbox/blocked"));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  const raw = (r.body as Record<string, unknown>)["blocked"];
  if (!Array.isArray(raw)) return { ok: true, blocked: [] };
  return {
    ok: true,
    blocked: raw.flatMap((entry) => {
      const row = (entry ?? {}) as Record<string, unknown>;
      const id = typeof row["block_id"] === "string" ? row["block_id"] : "";
      // A row with no id has no Unblock button that could work, so it is not a row.
      if (!id) return [];
      const handle = typeof row["handle"] === "string" ? bareHandle(row["handle"]) : "";
      const name = typeof row["display_name"] === "string" ? row["display_name"].trim() : "";
      return [
        {
          block_id: id,
          handle,
          // Never blank: the list has to call them something, and the handle is
          // the truth where a name was never given.
          display_name: name || (handle ? `@${handle}` : "Someone you blocked"),
          blocked_at: typeof row["blocked_at"] === "string" ? row["blocked_at"] : null,
        },
      ];
    }),
  };
}

export type UnblockResult =
  { ok: true } | { ok: false; code: "UNAUTHENTICATED" | "BLOCK_NOT_FOUND" | "UNKNOWN" };

export async function unblockSender(input: { block_id: string }): Promise<UnblockResult> {
  const r = await call(() => post("/api/v1/inbox/blocked/unblock", { block_id: input.block_id }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    if (r.res.status === 404) return { ok: false, code: "BLOCK_NOT_FOUND" };
    return { ok: false, code: "UNKNOWN" };
  }
  return { ok: true };
}

export type InboxFeedbackResult = { ok: true } | { ok: false; code: "UNAUTHENTICATED" | "UNKNOWN" };

/** Rates the ranking, not the sender (FR-6.4) — a signal for FR-8, never a rule. */
export async function submitInboxFeedback(input: {
  knock_id: string;
  helpful: boolean;
  reason_chip?: InboxReasonChip;
}): Promise<InboxFeedbackResult> {
  const body: Record<string, unknown> = { knock_id: input.knock_id, helpful: input.helpful };
  if (input.reason_chip) body["reason_chip"] = input.reason_chip;
  const r = await call(() => post("/api/v1/inbox/feedback", body));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  return { ok: true };
}
